import React from 'react';

import { useNavigate } from 'react-router-dom';

import { useModalContext } from '~/contexts/modal/ModalContext';
import { CancellationPath } from '~/modules/cancellation/constants';
import { SCREEN } from '~/modules/cancellation/views/variants/v3';
import DeleteAllDataConfirmationModal from '~/modules/settings/components/modals/DeleteAllDataConfirmationModal';
import DeleteAllDataModal from '~/modules/settings/components/modals/DeleteAllDataModal';
import DeleteAllDataSuccessModal from '~/modules/settings/components/modals/DeleteAllDataSuccessModal';
import SubscriptionCancelRequiredModal from '~/modules/settings/components/modals/SubscriptionCancelRequiredModal';
import { PersonalInformationPath } from '~/modules/settings/constants';
import useDeleteAccountBehavior from '~/modules/settings/hooks/useDeleteAccountBehavior';
import { ProjectBehaviorStrategy } from '~/projects/ProjectBehaviorStrategy';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { useAppSelector } from '~/store/hooks/useAppSelector';

const useDeleteAccount = (): {
  showDeleteAccountModal: () => void;
  isShownDeleteButton: boolean;
} => {
  const profile = useAppSelector((state) => state.profile);
  const { signOut } = useActions();
  const navigate = useNavigate();
  const { handleOpenModal } = useModalContext();

  const { shouldShowDeleteAccountButton: isShownDeleteButton, accountFlow } =
    useDeleteAccountBehavior({
      isAvailableForAllUsers:
        ProjectBehaviorStrategy.isDeleteAccountAvailableForAllUsers,
      subscriptionStatus: profile.user_subscription.status,
      signUpAt: profile.extra_fields.sign_up_at,
    });

  const showCancelSubscriptionModal = (): void => {
    analytics.trackEvent('data removal - need cancel before');
    handleOpenModal({
      hideCloseButton: true,
      maxWidth: '460px',
      component: ({ onClose }) => (
        <SubscriptionCancelRequiredModal
          onCancel={onClose}
          onSubmit={() => {
            onClose();
            navigate(
              {
                pathname: CancellationPath,
                search: `?screen=${SCREEN.CANCEL_FORM}`,
              },
              {
                state: {
                  from: PersonalInformationPath,
                },
              },
            );
          }}
        />
      ),
    });
  };
  //
  const showSuccessDeletionAccountModal = (): void => {
    analytics.trackEvent('data removal - removal success');
    handleOpenModal({
      hideCloseButton: true,
      disableBackdropClick: true,
      maxWidth: '460px',
      component: ({ onClose }) => (
        <DeleteAllDataSuccessModal
          onClickGotIt={onClose}
          signOut={signOut}
          closeModal={onClose}
        />
      ),
    });
  };
  //
  const showConfirmDeleteDataModal = (): void => {
    analytics.trackEvent('data removal - confirmation window');
    handleOpenModal({
      hideCloseButton: true,
      maxWidth: '460px',
      component: ({ onClose }) => (
        <DeleteAllDataConfirmationModal
          onCancel={onClose}
          onSuccessDeletion={showSuccessDeletionAccountModal}
        />
      ),
    });
  };

  const showDeleteDataModal = (): void => {
    analytics.trackEvent('data removal - information window');
    handleOpenModal({
      hideCloseButton: true,
      maxWidth: '460px',
      component: ({ onClose }) => (
        <DeleteAllDataModal
          onCancel={onClose}
          onSubmit={showConfirmDeleteDataModal}
        />
      ),
    });
  };

  const showDeleteAccountModal = (): void => {
    analytics.trackEvent('general - click delete profile');

    if (accountFlow === 'delete-account') {
      showDeleteDataModal();
    } else {
      showCancelSubscriptionModal();
    }
  };

  return {
    showDeleteAccountModal,
    isShownDeleteButton,
  };
};

export default useDeleteAccount;
